import { useState, type FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ClipboardCheck, ArrowLeft, CheckCheck } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

type Conteo = {
  id: string; folio: string; fecha_planeada: string; tipo: string; estado: string;
  total_articulos: number; articulos_contados: number; diferencias_total: number;
  almacen_clave: string; almacen_nombre: string;
};

export function ConteoFisicoListPage() {
  const [estado, setEstado] = useState('');
  const [openNew, setOpenNew] = useState(false);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['conteos', estado],
    queryFn: () => {
      const p = new URLSearchParams();
      if (estado) p.set('estado', estado);
      return api<Conteo[]>(`/inventario/conteos?${p}`);
    },
  });
  const items = data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Almacén · Toma física</p>
          <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Conteos físicos</h1>
          <p className="mt-1 text-[14px] text-ink-mute">
            Conteos cíclicos, anuales o selectivos. Al cerrar se generan movimientos de ajuste automáticamente.
          </p>
        </div>
        <Button onClick={() => setOpenNew(true)}><Plus size={16} /> Nuevo conteo</Button>
      </div>

      <div className="flex items-center gap-1 rounded-md border border-border bg-surface p-0.5 text-[12px] font-medium w-fit">
        {['', 'planeado', 'en_conteo', 'cerrado', 'cancelado'].map(e => (
          <button key={e || 'todas'} onClick={() => setEstado(e)}
            className={cn('rounded px-3 py-1.5 capitalize', estado === e ? 'bg-brand-500 text-white' : 'text-ink-mute hover:bg-muted')}>
            {e ? e.replace(/_/g, ' ') : 'todas'}
          </button>
        ))}
      </div>

      <Card>
        <CardBody className="p-0">
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <ClipboardCheck className="h-7 w-7 text-ink-soft" />
              <p className="text-[13px] text-ink-mute">Sin conteos físicos.</p>
            </div>
          ) : (
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                <tr>
                  <th className="px-5 py-3">Folio</th>
                  <th className="px-5 py-3">Almacén</th>
                  <th className="px-5 py-3">Tipo</th>
                  <th className="px-5 py-3">Planeado</th>
                  <th className="px-5 py-3 text-right">Avance</th>
                  <th className="px-5 py-3 text-right">Diferencias</th>
                  <th className="px-5 py-3">Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map(c => {
                  const pct = c.total_articulos > 0 ? (c.articulos_contados * 100 / c.total_articulos) : 0;
                  return (
                    <tr key={c.id} className="hover:bg-muted/50">
                      <td className="num px-5 py-3 font-semibold">{c.folio}</td>
                      <td className="px-5 py-3">{c.almacen_clave} — {c.almacen_nombre}</td>
                      <td className="px-5 py-3 capitalize">{c.tipo}</td>
                      <td className="num px-5 py-3 text-[11px] text-ink-mute">{fmt.date(c.fecha_planeada)}</td>
                      <td className="num px-5 py-3 text-right">{c.articulos_contados}/{c.total_articulos} ({pct.toFixed(0)}%)</td>
                      <td className={cn('num px-5 py-3 text-right font-semibold',
                        c.diferencias_total < 0 ? 'text-rose-700' : c.diferencias_total > 0 ? 'text-emerald-700' : '')}>
                        {fmt.money(c.diferencias_total)}
                      </td>
                      <td className="px-5 py-3"><EstadoPill e={c.estado} /></td>
                      <td className="px-5 py-3 text-right">
                        <Link to={`/app/conteos/${c.id}`}><Button variant="ghost" size="sm">Ver</Button></Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <NuevoConteoModal open={openNew} onClose={() => setOpenNew(false)}
        onCreated={() => { setOpenNew(false); qc.invalidateQueries({ queryKey: ['conteos'] }); }} />
    </div>
  );
}

function EstadoPill({ e }: { e: string }) {
  const cfg: Record<string, string> = {
    planeado: 'bg-stone-100 text-ink-mute',
    en_conteo: 'bg-sky-50 text-sky-700',
    cerrado: 'bg-emerald-50 text-emerald-700',
    cancelado: 'bg-rose-50 text-rose-700',
  };
  return <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', cfg[e] ?? 'bg-muted')}>{e.replace(/_/g, ' ')}</span>;
}

function NuevoConteoModal({ open, onClose, onCreated }: any) {
  const [almID, setAlmID] = useState('');
  const [tipo, setTipo] = useState('ciclico');
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  const { data: almacenes } = useQuery({
    queryKey: ['almacenes'], enabled: open,
    queryFn: () => api<any[]>('/almacenes'),
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubm(true); setErr(null);
    try {
      await api('/inventario/conteos', {
        method: 'POST',
        body: {
          almacen_id: almID,
          tipo,
          fecha_planeada: new Date(fecha).toISOString(),
        },
      });
      onCreated();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Nuevo conteo físico"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="ct-form" loading={subm}>Iniciar conteo</Button>
      </>}>
      <form id="ct-form" onSubmit={submit} className="flex flex-col gap-3">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}
        <div>
          <label className="text-[13px] font-medium">Almacén</label>
          <select required value={almID} onChange={(e) => setAlmID(e.target.value)}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="">(selecciona)</option>
            {(almacenes ?? []).map((a: any) => (
              <option key={a.id} value={a.id}>{a.clave} — {a.nombre}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[13px] font-medium">Tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}
            className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
            <option value="ciclico">Cíclico</option>
            <option value="anual">Anual</option>
            <option value="selectivo">Selectivo</option>
          </select>
        </div>
        <Input name="fecha" label="Fecha planeada" type="date" required
          value={fecha} onChange={(e) => setFecha(e.target.value)} />
        <div className="rounded-md bg-amber-50 p-3 text-[12px] text-amber-900">
          Al crear se toma snapshot de la existencia actual de todos los artículos del almacén.
          Después podrás capturar la cantidad física y al cerrar se generan ajustes automáticos.
        </div>
      </form>
    </Modal>
  );
}

// =========================================================================
// Detalle
// =========================================================================

export function ConteoFisicoDetallePage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { data: c } = useQuery<any>({ queryKey: ['conteo', id], queryFn: () => api(`/inventario/conteos/${id}`) });
  const [filter, setFilter] = useState('');

  async function capturar(artID: string, fisica: number) {
    try {
      await api(`/inventario/conteos/${id}/capturar`, {
        method: 'POST',
        body: { articulo_id: artID, cantidad_fisica: fisica },
      });
      qc.invalidateQueries({ queryKey: ['conteo', id] });
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }

  async function cerrar() {
    if (!confirm('¿Cerrar conteo y generar movimientos de ajuste?')) return;
    try {
      const res = await api<any>(`/inventario/conteos/${id}/cerrar`, { method: 'POST' });
      alert(`Cerrado. Sobrantes: ${res.sobrantes_lineas}, faltantes: ${res.faltantes_lineas}, valor neto ${res.valor_neto}`);
      qc.invalidateQueries({ queryKey: ['conteo', id] });
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }

  if (!c) return <div className="p-10 text-[13px] text-ink-mute">Cargando…</div>;

  const lineas = (c.lineas ?? []).filter((l: any) =>
    !filter || l.articulo_clave?.toLowerCase().includes(filter.toLowerCase()) ||
    l.articulo_descripcion?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/app/conteos" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-mute hover:text-ink">
          <ArrowLeft size={14} /> Volver a conteos
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <div>
            <p className="num text-[12px] font-semibold uppercase tracking-[0.14em] text-brand-700">{c.folio}</p>
            <h1 className="font-display text-[24px] font-bold tracking-tight text-ink">Conteo físico {c.almacen_nombre}</h1>
            <p className="text-[12px] text-ink-mute capitalize">{c.tipo} · {c.estado} · {fmt.date(c.fecha_planeada)}</p>
          </div>
          {c.estado === 'en_conteo' && (
            <Button onClick={cerrar}><CheckCheck size={14} /> Cerrar conteo</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label="Artículos" value={`${c.articulos_contados}/${c.total_articulos}`} />
        <Stat label="Avance" value={c.total_articulos > 0 ? `${((c.articulos_contados * 100) / c.total_articulos).toFixed(0)}%` : '—'} />
        <Stat label="Diferencias netas" value={fmt.money(c.diferencias_total ?? 0)} />
      </div>

      <Card>
        <CardHeader title="Artículos del conteo" action={
          <Input name="filter" label="" placeholder="filtrar…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        } />
        <CardBody className="p-0">
          <table className="min-w-full text-[13px]">
            <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
              <tr>
                <th className="px-5 py-3">Clave</th>
                <th className="px-5 py-3">Descripción</th>
                <th className="px-5 py-3 text-right">Sistema</th>
                <th className="px-5 py-3 text-right">Físico</th>
                <th className="px-5 py-3 text-right">Diferencia</th>
                <th className="px-5 py-3 text-right">Valor dif.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {lineas.map((l: any) => (
                <tr key={l.articulo_id}>
                  <td className="num px-5 py-3">{l.articulo_clave}</td>
                  <td className="px-5 py-3 max-w-[300px] truncate">{l.articulo_descripcion}</td>
                  <td className="num px-5 py-3 text-right">{fmt.num(l.cantidad_sistema)}</td>
                  <td className="px-5 py-3 text-right">
                    {c.estado === 'en_conteo' ? (
                      <input type="number" step="0.001"
                        defaultValue={l.cantidad_fisica ?? ''}
                        onBlur={(e) => {
                          const v = parseFloat(e.target.value);
                          if (!isNaN(v) && v !== l.cantidad_fisica) capturar(l.articulo_id, v);
                        }}
                        className="num h-8 w-24 rounded border border-border bg-surface px-2 text-right" />
                    ) : (
                      <span className="num">{l.cantidad_fisica != null ? fmt.num(l.cantidad_fisica) : '—'}</span>
                    )}
                  </td>
                  <td className={cn('num px-5 py-3 text-right font-semibold',
                    l.diferencia < 0 ? 'text-rose-700' : l.diferencia > 0 ? 'text-emerald-700' : '')}>
                    {l.diferencia !== 0 ? fmt.num(l.diferencia) : ''}
                  </td>
                  <td className={cn('num px-5 py-3 text-right',
                    l.valor_diferencia < 0 ? 'text-rose-700' : l.valor_diferencia > 0 ? 'text-emerald-700' : '')}>
                    {l.valor_diferencia !== 0 ? fmt.money(l.valor_diferencia) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-5 shadow-card">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
      <div className="num mt-2 text-[20px] font-semibold text-ink">{value}</div>
    </div>
  );
}
